import React, { useEffect, useState } from "react";
import { Settings } from "lucide-react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function ReactiveSettingsPage() {
  const { t } = useTranslation();
  const { deviceId } = useParams();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  // 🔹 Veriyi periyodik olarak getir
  useEffect(() => {
    if (!deviceId) return;

    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(
          `${API_BASE}/reactive/settings?deviceId=${deviceId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const json = await res.json();
        setSettings(json);
        setLastUpdate(new Date().toLocaleTimeString());
      } catch (err) {
        console.error("⚠️ Reactive settings fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    const i = setInterval(fetchData, 10000);
    return () => clearInterval(i);
  }, [API_BASE, deviceId]);

  // 🔹 Alan listesi + anlam map'leri
  const fields = [
    { key: "MB_ADDR_VTR_C", label: "reactiveSettings.voltageTransformerRatio", color: "#fb923c" },
    {
      key: "MB_ADDR_VTR_TYPE_C",
      label: "reactiveSettings.voltageTransformerType",
      color: "#f97316",
      map: {
        0: "y0/d0 TCR",
        1: "Dyn5",
        2: "Dyn11",
        3: "Yzn11",
        4: "y0/d0",
        5: "Std",
      },
    },
    { key: "MB_ADDR_CTR_C", label: "reactiveSettings.currentTransformerRatio", color: "#f97316" },
    {
      key: "MB_ADDR_CTR_TYPE_C",
      label: "reactiveSettings.currentTransformerType",
      color: "#f59e0b",
      map: { 0: "X5", 1: "X1" },
    },
    { key: "MB_ADDR_STEP_TIME_ALMA_C", label: "reactiveSettings.stepTakeTime", color: "#fbbf24" },
    { key: "MB_ADDR_STEP_TIME_BIRAKMA_C", label: "reactiveSettings.stepReleaseTime", color: "#fbbf24" },
    { key: "MB_ADDR_STEP_TIME_DESARJ_C", label: "reactiveSettings.dischargeTime", color: "#fbbf24" },
    { key: "MB_ADDR_STEP_TIME_GECIS_C", label: "reactiveSettings.stepTransitionTime", color: "#facc15" },
    { key: "MB_ADDR_TARGET_RATIO_C", label: "reactiveSettings.targetRatio", color: "#84cc16" },
    { key: "MB_ADDR_TARGET_TOLERANCE_C", label: "reactiveSettings.targetTolerance", color: "#22c55e" },
    { key: "MB_ADDR_TARGET_OFFSET_L1_C", label: "reactiveSettings.targetL1Offset", color: "#10b981" },
    { key: "MB_ADDR_TARGET_OFFSET_L2_C", label: "reactiveSettings.targetL2Offset", color: "#14b8a6" },
    { key: "MB_ADDR_TARGET_OFFSET_L3_C", label: "reactiveSettings.targetL3Offset", color: "#0ea5e9" },
    {
      key: "MB_ADDR_TARGET_PRC_ACTIVE_C",
      label: "reactiveSettings.prcActive",
      color: "#38bdf8",
      map: { 0: "reactiveSettings.passive", 1: "reactiveSettings.active" },
    },
    {
      key: "MB_ADDR_TARGET_TCR_ACTIVE_C",
      label: "reactiveSettings.tcrActive",
      color: "#60a5fa",
      map: { 0: "reactiveSettings.passive", 1: "reactiveSettings.active" },
    },
    {
      key: "MB_ADDR_TARGET_JEN_ACTIVE_C",
      label: "reactiveSettings.jenActive",
      color: "#818cf8",
      map: { 0: "reactiveSettings.passive", 1: "reactiveSettings.active" },
    },
    {
      key: "MB_ADDR_TARGET_OFFSET_ACTIVE_C",
      label: "reactiveSettings.offsetActive",
      color: "#a78bfa",
      map: { 0: "reactiveSettings.passive", 1: "reactiveSettings.active" },
    },
    {
      key: "MB_ADDR_PHASE_DIRECTION_C",
      label: "reactiveSettings.phaseDirection",
      color: "#c084fc",
      map: {
        0: "reactiveSettings.allPhasesPositive",
        1: "reactiveSettings.l1Negative",
        2: "reactiveSettings.l2Negative",
        3: "reactiveSettings.l1l2Negative",
        4: "reactiveSettings.l3Negative",
        5: "reactiveSettings.l1l3Negative",
        6: "reactiveSettings.l2l3Negative",
        7: "reactiveSettings.allPhasesNegative",
      },
    },
    {
      key: "MB_ADDR_DRIVE_METHOD_C",
      label: "reactiveSettings.drivingMethod",
      color: "#e879f9",
      map: { 0: "reactiveSettings.automatic", 1: "reactiveSettings.manual" },
    },
  ];

  // 🔹 UI render
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex flex-col items-center py-12 px-4">
      <div className="w-full max-w-6xl">
        {/* Header */}
        <div className="flex items-center gap-3 mb-10">
          <div className="p-2 bg-orange-100 rounded-lg">
            <Settings className="text-orange-600 w-6 h-6" />
          </div>
          <h1 className="text-2xl font-semibold text-gray-800">
            {t("reactiveSettings.title")}
          </h1>
          {lastUpdate && (
            <span className="ml-auto text-sm text-gray-500">
              {t("reactiveSettings.lastUpdate")}:{" "}
              <span className="text-orange-600 font-medium">{lastUpdate}</span>
            </span>
          )}
        </div>

        {/* İçerik */}
        {loading ? (
          <p className="text-center text-gray-500 py-20 text-lg">
            {t("reactiveSettings.loading")}
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {fields.map((f) => {
              const rawValue = settings[f.key];
              const displayValue =
                f.map && rawValue in f.map
                  ? (f.map[rawValue].includes("reactiveSettings.") ? t(f.map[rawValue]) : f.map[rawValue])
                  : rawValue ?? "—";

              return (
                <div
                  key={f.key}
                  className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all p-6 relative"
                >
                  <span
                    className="absolute left-0 top-0 h-full w-1.5 rounded-l-xl"
                    style={{ backgroundColor: f.color }}
                  />
                  <h3 className="text-base font-semibold text-gray-800 mb-1">
                    {t(f.label)}
                  </h3>
                  <p className="text-2xl font-semibold text-gray-700 mb-1">
                    {displayValue}
                  </p>
                  <p className="text-xs text-gray-500">
                    {displayValue !== "—" ? t("reactiveSettings.currentValue") : t("reactiveSettings.noData")}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
